import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--off-white);
`;

const HeroSection = styled.section`
  background: var(--carbon-gradient);
  padding: var(--space-16) 0;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-6);
`;

const HeroTitle = styled.h1`
  color: var(--white);
  font-size: var(--text-4xl);
  font-weight: var(--font-black);
  margin-bottom: var(--space-4);
  
  @media (max-width: 768px) {
    font-size: var(--text-3xl);
  }
`;

const HeroSubtitle = styled.p`
  color: var(--chrome-silver);
  font-size: var(--text-lg);
  font-weight: var(--font-medium);
  max-width: 600px;
  margin: 0 auto;
  line-height: var(--leading-relaxed);
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: var(--space-16) var(--space-6);
  
  @media (max-width: 768px) {
    padding: var(--space-12) var(--space-4);
  }
`;

const Section = styled.section`
  background: var(--white);
  border-radius: var(--radius-2xl);
  padding: var(--space-10);
  box-shadow: var(--shadow-lg);
  margin-bottom: var(--space-8);
  
  @media (max-width: 768px) {
    padding: var(--space-8);
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  color: var(--carbon-black);
  margin-bottom: var(--space-6);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 40px;
    height: 3px;
    background: var(--racing-gradient);
    border-radius: var(--radius-full);
  }
`;

const SectionContent = styled.div`
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  color: var(--dark-gray);
  
  p {
    margin-bottom: var(--space-4);
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul, ol {
    margin: var(--space-4) 0;
    padding-left: var(--space-6);
    
    li {
      margin-bottom: var(--space-2);
    }
  }
  
  strong {
    color: var(--carbon-black);
    font-weight: var(--font-semibold);
  }
`;

const LastUpdated = styled.div`
  background: var(--light-gray);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  text-align: center;
  font-size: var(--text-sm);
  color: var(--medium-gray);
  margin-bottom: var(--space-8);
`;

const ContactBox = styled.div`
  background: var(--racing-gradient);
  color: var(--white);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  text-align: center;
  margin-top: var(--space-8);
`;

const ContactTitle = styled.h3`
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  margin-bottom: var(--space-3);
`;

const ContactText = styled.p`
  margin-bottom: var(--space-3);
  opacity: 0.9;
`;

const ContactEmail = styled.a`
  color: var(--white);
  font-weight: var(--font-semibold);
  text-decoration: underline;
  
  &:hover {
    color: var(--chrome-silver);
  }
`;

const TermsPage: React.FC = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>Terms of Service</HeroTitle>
          <HeroSubtitle>
            Please read these terms carefully before using our services. 
            By accessing CarBuzz.fit, you agree to be bound by these terms.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentContainer>
        <LastUpdated>
          Last updated: January 1, 2024
        </LastUpdated>

        <Section>
          <SectionTitle>Agreement to Terms</SectionTitle>
          <SectionContent>
            <p>
              These Terms of Service ("Terms") govern your use of our website located at carbuzz.fit 
              (the "Service") operated by CarBuzz.fit ("us", "we", or "our"). Our Privacy Policy also 
              governs your use of the Service and explains how we collect, safeguard and disclose 
              information that results from your use of our web pages.
            </p>
            <p>
              Your agreement with us includes these Terms and our Privacy Policy ("Agreements"). You 
              acknowledge that you have read and understood Agreements, and agree to be bound by them. 
              If you do not agree with (or cannot comply with) Agreements, then you may not use the 
              Service, but please let us know by emailing legal@carbuzz.fit so we can try to find a solution.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Acceptable Use</SectionTitle>
          <SectionContent>
            <p>
              You may use our Service for lawful purposes only. You agree not to use the Service:
            </p>
            <ul>
              <li>In any way that violates any applicable national or international law or regulation</li>
              <li>To transmit, or procure the sending of, any advertising or promotional material without our prior written consent</li>
              <li>To impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person or entity</li>
              <li>In any way that infringes upon the rights of others, or in any way is illegal, threatening, fraudulent, or harmful</li>
              <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service</li>
            </ul>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Content</SectionTitle>
          <SectionContent>
            <p><strong>Our Content</strong></p>
            <p>
              The Service and its original content, features and functionality are and will remain the 
              exclusive property of CarBuzz.fit and its licensors. The Service is protected by copyright, 
              trademark, and other laws. Our trademarks may not be used in connection with any product 
              or service without our prior written consent.
            </p>

            <p><strong>Your Content</strong></p>
            <p>
              Our Service may allow you to post, link, store, share and otherwise make available certain 
              information, text, graphics, videos, or other material ("Content"). You are responsible 
              for Content that you post to the Service, including its legality, reliability, and 
              appropriateness. By posting Content to the Service, You represent and warrant that:
            </p>
            <ul>
              <li>Content is yours (you own it) and/or you have the right to use it</li>
              <li>Use of your Content on our Service will not infringe, violate or misappropriate the rights of any third party</li>
            </ul>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Prohibited Uses</SectionTitle>
          <SectionContent>
            <p>
              You may not use our Service:
            </p>
            <ul>
              <li>For any unlawful purpose or to solicit others to take or refrain from taking unlawful action</li>
              <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
              <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
              <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
              <li>To submit false or misleading information</li>
              <li>To upload or transmit viruses or any other type of malicious code</li>
              <li>To collect or track the personal information of others</li>
              <li>To spam, phish, pharm, pretext, spider, crawl, or scrape</li>
              <li>For any obscene or immoral purpose</li>
              <li>To interfere with or circumvent the security features of the Service</li>
            </ul>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Accounts</SectionTitle>
          <SectionContent>
            <p>
              When you create an account with us, you guarantee that you are above the age of 18, and 
              that the information you provide us is accurate, complete, and current at all times. 
              Inaccurate, incomplete, or obsolete information may result in the immediate termination 
              of your account on the Service.
            </p>
            <p>
              You are responsible for maintaining the confidentiality of your account and password, 
              including but not limited to the restriction of access to your computer and/or account. 
              You agree to accept responsibility for any and all activities or actions that occur under 
              your account and/or password.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Intellectual Property Rights</SectionTitle>
          <SectionContent>
            <p>
              The Service and its original content, features and functionality are and will remain the 
              exclusive property of CarBuzz.fit and its licensors. The Service is protected by copyright, 
              trademark, and other laws of both the Country and foreign countries. Our trademarks and 
              trade dress may not be used in connection with any product or service without our prior 
              written consent.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>User Comments, Feedback and Other Submissions</SectionTitle>
          <SectionContent>
            <p>
              If, at our request, you send certain specific submissions (for example contest entries) 
              or without a request from us you send creative ideas, suggestions, proposals, plans, or 
              other materials, whether online, by email, by postal mail, or otherwise (collectively, 
              'comments'), you agree that we may, at any time, without restriction, edit, copy, publish, 
              distribute, translate and otherwise use in any medium any comments that you forward to us.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Privacy Policy</SectionTitle>
          <SectionContent>
            <p>
              Your privacy is important to us. Please review our Privacy Policy, which also governs 
              your use of the Service, to understand our practices.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Termination</SectionTitle>
          <SectionContent>
            <p>
              We may terminate or suspend your account and bar access to the Service immediately, 
              without prior notice or liability, under our sole discretion, for any reason whatsoever 
              and without limitation, including but not limited to a breach of the Terms.
            </p>
            <p>
              If you wish to terminate your account, you may simply discontinue using the Service.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Disclaimer</SectionTitle>
          <SectionContent>
            <p>
              The information on this website is provided on an "as is" basis. To the fullest extent 
              permitted by law, this Company excludes all representations, warranties, conditions and 
              terms whether express or implied, statutory or otherwise, including without limitation 
              the implied warranties of merchantability, fitness for a particular purpose and 
              non-infringement.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Limitation of Liability</SectionTitle>
          <SectionContent>
            <p>
              In no event shall CarBuzz.fit, nor its directors, employees, partners, agents, suppliers, 
              or affiliates, be liable for any indirect, incidental, special, consequential or punitive 
              damages, including without limitation, loss of profits, data, use, goodwill, or other 
              intangible losses, resulting from your use of the Service.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Governing Law</SectionTitle>
          <SectionContent>
            <p>
              These Terms shall be interpreted and governed by the laws of the State of California, 
              without regard to its conflict of law provisions. Our failure to enforce any right or 
              provision of these Terms will not be considered a waiver of those rights.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Changes to Terms</SectionTitle>
          <SectionContent>
            <p>
              We reserve the right, at our sole discretion, to modify or replace these Terms at any 
              time. If a revision is material, we will provide at least 30 days notice prior to any 
              new terms taking effect. What constitutes a material change will be determined at our 
              sole discretion.
            </p>
            <p>
              By continuing to access or use our Service after any revisions become effective, you 
              agree to be bound by the revised terms.
            </p>
          </SectionContent>
        </Section>

        <ContactBox>
          <ContactTitle>Questions About These Terms?</ContactTitle>
          <ContactText>
            If you have any questions about these Terms of Service, please contact us at:
          </ContactText>
          <ContactEmail href="mailto:legal@carbuzz.fit">
            legal@carbuzz.fit
          </ContactEmail>
        </ContactBox>
      </ContentContainer>
    </PageContainer>
  );
};

export default TermsPage;
